/**
 * Car — 기종·작업시간·옵션 **문구** 유틸.
 *
 * 기종표(코드 → 업종/분류/톤·미터)는 이 파일에 없다. 서버가 `/asset/bootstrap` 으로
 * `g5_car_master` 를 그대로 실어 `G5.CARMASTER` 에 넣고(레이아웃이 main.js 앞에 로드),
 * 여기서는 읽기만 한다.
 *
 * 예전에는 66행을 이 파일에 손으로 베껴 뒀다. 그 결과 DB 와 26행이 어긋난 채 돌아갔고
 * (리프트 20건은 미터를 톤으로 표기 — 서버는 '1인승 6m', 여기서는 '1인승 6T'),
 * 기종을 한 줄 추가하면 이 파일도 같이 고쳐야 했으며 안 고치면 `undefined.split` 으로
 * 오더목록 렌더가 통째로 죽었다.
 *
 * @see app/Common/ClientBootstrap.php         내려보내는 쪽
 * @see app/Services/Car/CarCodeService.php    같은 파싱의 PHP 정본 (parseCarRange)
 * @see app/Services/Order/OrderFormatService.php  작업시간 문구의 PHP 짝
 */
import Util from '../Common/Util';


// 지게차 작업 1시간 단위를 부르는 분 — 지게차는 40분 작업이 기본이다 (hourLabel)
const FORKLIFT_MIN_PER_HOUR = 40;

export default class Car {
    constructor() {
        // row() 의 코드 인덱스. 어떤 master 배열로 만들었는지 함께 들고 있다가
        // (defer 로딩 등으로) 마스터가 나중에 실리면 다시 만든다.
        this._index = null;
        this._indexSrc = null;
    }

    // ─────────────────────────────────────────────────────────────
    // 마스터 접근
    // ─────────────────────────────────────────────────────────────

    /** 서버가 심어준 기종 마스터. 스크립트가 안 실린 페이지에서도 죽지 않게 빈 값으로 떨어진다. */
    all() {
        const g = (typeof G5 !== 'undefined') ? G5 : {};
        return g.CARMASTER || { master: [], option: {}, ladder: {} };
    }

    /** 업종 목록 (스카이, 사다리, …). 정본은 PHP `G5_CARTYPE_KR` — 서버가 G5 에 심는다 */
    types() {
        return (typeof G5 !== 'undefined' && G5['CARTYPE_KR']) || [];
    }

    /** 업종 한글명 → 영문명 (스카이 → sky). CSS 클래스·이미지 파일명이 영문명을 쓴다 */
    engName(kor) {
        const en = (typeof G5 !== 'undefined' && G5['CARTYPE_EN']) || [];
        return en[this.types().indexOf(kor)] || '';
    }

    /** 기종 전체 행 */
    master() {
        return this.all().master;
    }

    /** 업종의 선택옵션 CSV ('굴절,단축,윈찌'). 옵션이 없는 업종은 빈 문자열 */
    options(carType) {
        return this.all().option[carType] || '';
    }

    /**
     * 기종코드 한 행.
     *
     * 없으면 null 이다 — 마스터에서 지운 코드가 옛 오더에 남아 있어도 화면이 죽으면 안 된다.
     * @returns {{code:number,car_type:string,ca1:string,ca2:string,eng_name:string,size_list:string}|null}
     */
    row(code) {
        const list = this.master();
        if (this._indexSrc !== list) {
            this._indexSrc = list;
            this._index = {};
            list.forEach((r) => { this._index[r.code] = r; });
        }
        return this._index[Util.int(code)] || null;
    }

    /** 규격을 미터(스카이)·층(사다리)으로 고르는 업종인가 — car_range 에 `|미터` 가 붙는 업종 */
    isMeterType(carType) {
        return carType === '스카이' || carType === '사다리';
    }

    /** 사다리 층 → 미터. 정본은 CarCodeService::getMeterFloorMap() */
    floorToMeter(floor) {
        return this.all().ladder[Util.int(floor)] || '';
    }

    /** 사다리 미터 → 층. 표에 없는 미터는 0 */
    meterToFloor(meter) {
        const map = this.all().ladder;
        for (const floor in map) {
            if (map[floor] == meter) return Util.int(floor);
        }
        return 0;
    }

    /**
     * 코드번호로부터 기종명을 얻는다. (17T, 03W, 6m …)
     * @returns {string} 마스터에 없는 코드면 빈 문자열
     */
    getNameFromCode(code) {
        const r = this.row(code);
        return r ? r.eng_name : '';
    }

    // ─────────────────────────────────────────────────────────────
    // 작업시간 문구
    // ─────────────────────────────────────────────────────────────

    /**
     * 작업시간 문구.
     * PHP 짝: OrderFormatService::getWorkHourStr()
     * @param order
     * @param isFull 추가시간까지 붙일지
     * @param byCarType 장비별 시간 표기를 쓸지 (hourLabel — 지게차는 분). 화면은 켠 채로 둔다
     * @returns {string}
     */
    getWorkHourStr(order, isFull = true, byCarType = true) {
        const carType = byCarType ? order['car_type'] : '';
        let r = this.getWorkHourText(order['car_workhour'], order['car_workdaytype'], carType);
        const [addHour, addType] = this.addHourFields(order);
        if (addHour > 0 && isFull) {
            const ar = this.getWorkHourText(addHour, addType == 1 ? '시간' : '야간', carType);
            r += ` (추가 ${ar})`;
        }
        return r;
    }

    /**
     * 시간 수량 → 문구. 지게차는 작업 1시간을 40분으로 부른다 (1시간 → 40분, 3시간 → 120분).
     * **표시만** 바꾼다 — 저장값(car_workhour)·단가계산은 시간 그대로다.
     * PHP 짝: OrderFormatService::hourLabel
     */
    hourLabel(n, carType = '') {
        return carType === '지게차' ? `${n * FORKLIFT_MIN_PER_HOUR}분` : `${n}시간`;
    }

    /** 시간+단위 → 문구. PHP 짝: OrderFormatService::getWorkHourText(). carType 을 주면 시간을 장비별로 부른다(hourLabel) */
    getWorkHourText(n, t, carType = '') {
        let r = '';
        switch (t) {
            case '야간': r = `야간${this.hourLabel(n, carType)}`; break;
            case '시간': r = this.hourLabel(n, carType); break;
            case '하루':
            case '일': r = n + '일'; break;
            case '월': r = n + '개월'; break;
            case '오전':
            case '오후': r = t + this.hourLabel(4, carType); break;   // 반나절 = 4시간 고정 (지게차 160분)
            case '가구/가전':
            case '이삿짐':
            case '톤': r = n + '톤짐'; break;
        }
        return r;
    }

    /**
     * 추가시간 문구
     * @param order
     * @returns {string}
     */
    getAddHourStr(order) {
        let txt = '';
        const [addHour, addType] = this.addHourFields(order);
        if (Util.int(addHour) > 0) {
            const dn = addType == '1' ? '주간' : '야간';
            txt = `${dn} ${this.hourLabel(addHour, order['car_type'])}`;
        }
        return txt;
    }

    /**
     * 추가시간·추가유형 — [add_hour, add_hour_type].
     * 서버가 주는 오더 행은 새 칸 이름(add_hour/add_hour_type)이고, 발주 폼 객체(ORDER)는 요청 키
     * (car_workaddhor/car_workaddtype)를 그대로 쓴다. 두 모양을 다 받는다.
     */
    addHourFields(order) {
        const h = order['add_hour'] ?? order['car_workaddhor'];
        const t = order['add_hour_type'] ?? order['car_workaddtype'];
        return [h, t];
    }

    // ─────────────────────────────────────────────────────────────
    // 기종범위 · 옵션 문구
    // ─────────────────────────────────────────────────────────────

    /**
     * 장비 옵션 문구를 얻는다.
     *
     * TODO : 전고·이동거리도 car_option 에 포함시키는 형태로 디비구조를 변경하자.
     * @param order
     * @returns {string}
     */
    getCarOptionStr(order) {
        const jungo = order['car_height'];
        const distance = order['move_distance'];

        const opts = [];
        Util.nvl(order['car_option']).split(',').forEach((v) => {
            if (!v) return;
            opts.push(v.replace('[', '').replace(']', ''));
        });

        if (jungo) opts.push('전고:' + jungo);
        if (distance) {
            opts.push(isNaN(distance) ? distance : distance + 'Km이내');
        }

        return opts.toString();
    }

    /**
     * car_code, car_meter 로부터 car_range 를 얻는다.
     * @param car_code 예) 100
     * @param car_meter 예) 16
     * @returns {string} car_range 예) '100|16'. 코드가 없으면 빈 문자열
     */
    makeCarRange(car_code, car_meter) {
        if (!car_code) return '';
        return car_meter ? `${car_code}|${car_meter}` : String(car_code);
    }

    /**
     * car_range 로부터 정보를 얻는다.
     *
     * 차량코드|미터또는층의 범위
     *   110|24~30 : 차량코드110 이고 24M ~ 30M (스카이)
     *   250|64    : 차량코드250 이고 64M이상 (사다리)
     *   340~342   : 차량코드340 부터 342 (크레인)
     *   530~534   : 차량코드530 부터 534 (굴삭기)
     *   530,534   : 차량코드530,534 (굴삭기)
     *
     * PHP 짝: CarCodeService::parseCarRange()
     * @returns {{ca:string,tons:string[],meter:string,meterStyle:string,code_range:string[],meter_range:string[]}|null}
     */
    getInfoFromCarRange(data) {
        const car_type = data['car_type'];
        const car_range = data['car_range'] || this.makeCarRange(data['car_code'], data['car_meter']);
        if (!car_range) return null;

        const out = {
            'ca': '',
            'tons': [],
            'meter': '',
            'meterStyle': '',
            'code_range': [],
            'meter_range': [],
        };

        const [tonRange, meterRange] = String(car_range).split('|');

        // 기종범위
        if (tonRange) {
            if (tonRange.indexOf(',') == -1) {
                // 340(단일) 또는 340~340(단일) 또는 340~342(여러기종)
                const arr = tonRange.split('~');
                out['code_range'] = arr;

                const fr = this.row(arr[0]);
                const to = (arr[1] && arr[1] != arr[0]) ? this.row(arr[1]) : fr;
                if (fr) {
                    out['ca'] = fr.ca1;
                    out['tons'].push(to && to !== fr ? `${fr.eng_name} ~ ${to.eng_name}` : fr.eng_name);
                }
            } else {
                // 개별검색 530,534
                const codes = tonRange.split(',');
                out['code_range'] = codes;   // 범위분기와 같은 축으로 채운다 — CarSelect 가 3단을 되짚을 때 쓴다
                codes.forEach((v) => {
                    const r = this.row(v);
                    if (!r) return;
                    if (!out['ca']) out['ca'] = r.ca1;
                    out['tons'].push(r.eng_name);
                });
            }
        }

        // 미터범위 (스카이·사다리만 존재)
        if (meterRange && this.isMeterType(car_type)) {
            const list = meterRange.split('~');
            out['meter_range'] = list;
            const fr = list[0];
            const to = list[1];
            if (to && fr != to) {           // 이상~이하
                out['meter'] = `${fr}~${to}m`;
            } else {                        // 이상
                out['meter'] = `${fr}m`;
                if (car_type == '사다리') {
                    const fl = this.meterToFloor(fr);
                    out['meter'] = `${fl}층(${fr}m)`;
                    out['meterStyle'] = `<b style='font-weight:bold'>${fl}층</b>(${fr}m)`;
                }
            }
        }
        if (!out['meterStyle']) out['meterStyle'] = out['meter'];

        return out;
    }

    /**
     * 장비 기종 범위 문구를 얻는다.
     * @param order
     * @param style 1이면 스타일이 적용된 값(사다리 층수 굵게)
     * @returns {string}
     */
    getCarRangeStr(order, style = 0) {
        const r = this.getInfoFromCarRange(order);
        if (!r) return '';

        // 비어 있는 조각은 버린다 — 그냥 이으면 업종에 따라 앞뒤에 공백이 남는다
        return [r['ca'], r['tons'].join(','), style == 1 ? r['meterStyle'] : r['meter']]
            .filter((v) => v)
            .join(' ');
    }

    // ─────────────────────────────────────────────────────────────
    // 기타
    // ─────────────────────────────────────────────────────────────

    /**
     * work_time 값으로 시간 컬럼들의 정보를 만들어낸다.
     * @param work_time
     * @returns {{work_start_h:string, work_start_m:string, work_day:string}}
     */
    getTimeFields(work_time) {
        return {
            'work_start_h': dayjs(work_time).format('HH'),
            'work_start_m': dayjs(work_time).format('mm'),
            'work_day': dayjs(work_time).format('YYYY-MM-DD'),
        };
    }

    /**
     * 장비대금을 계산한다. (계산 자체는 서버 — App\Services\CarPrice\PriceCalculator)
     *
     * @param data {car_type, car_range, car_workhour, car_workdaytype, car_option,
     *              work_start_h, work_day, sosok}
     * @param func 결과 콜백
     */
    calcPrice(data, func) {
        if (typeof func != 'function') return;

        $.ajax({
            type: 'POST', data, dataType: 'json', url: '/m/ajax/price',
            success: (out) => func(out),
        });
    }

    /**
     * 스카이 저단가 여부 (2026-09-01~). 판정 정본은 PriceCalculator::isSkyLowPricePenalty.
     *
     * 하한(sky_low_limit)은 톤수·작업시간대로 정해지므로 **calcPrice 응답마다 새로 받아야 한다.**
     * 톤수만 바꾸고 하한을 안 갱신하면 옛 구간의 하한으로 판정한다.
     *
     * @param o {car_price, sky_low_limit} — 오더든 calcPrice 응답이든 두 값만 있으면 된다
     */
    isSkyLow(o) {
        const limit = Util.int(o?.['sky_low_limit']);
        const price = Util.int(o?.['car_price']);
        return !!(limit && price && price < limit);
    }

    /**
     * 저단가 **경고를 띄울** 자리인지. 판정(isSkyLow)과 나눠 둔 이유 —
     *
     * 업체발주자(협력사)는 본사와 합의된 계약단가로 발주하는데 그 단가가 하한 아래라,
     * 정책대상 조합이 거의 다 걸린다 (삼성은 2026-06~ 106건 중 104건). 수수료없음은
     * 서버가 그대로 적용하지만(PriceCalculator::isSkyLowPricePenalty — 소속을 안 가린다),
     * 정상 거래마다 경고를 띄우면 경고가 일상이 되어 정작 봐야 할 사람이 안 읽는다.
     *
     * 그래서 **결과는 그대로 두고 경고만 끈다.** 판정 자체를 끄면 수수료가 달라진다.
     */
    isSkyLowWarn(o) {
        return this.isSkyLowWarnMember() && this.isSkyLow(o);
    }

    /** 저단가 경고를 받는 회원인지 — 업체발주자(계약단가)는 제외. 이유는 isSkyLowWarn 참고 */
    isSkyLowWarnMember() {
        return typeof MEMBER !== 'undefined' && MEMBER['mem_driver_type'] != '업체발주자';
    }

}
